import * as React from 'react';

import ArrowLeft from '@crehana/icons/outline/ArrowIosLeft';
import ArrowRight from '@crehana/icons/outline/ArrowIosRight';
import { useIsClient, useMediaQuery } from '@crehana/react-hooks';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { IconButton } from '@crehana/ui';

import SliderItem from './CarouselItem';
import {
  DEFAULT_TRANSLATION_DESKTOP,
  DEFAULT_TRANSLATION_MOBILE,
} from './constants';
import Loader from './Loader';
import {
  DraggableContentContainer,
  SliderContainer,
  Wrapper,
} from './PacksCarousel.styles';
import type { SliderProps } from './types';
import { getTranslationInPx } from './utils';

export const Slider: React.FC<React.PropsWithChildren<SliderProps>> = ({
  courses,
}) => {
  const { matches: isSm } = useMediaQuery('sm');
  const { matches: isMd } = useMediaQuery('md');
  const { matches: isLg } = useMediaQuery('lg');
  const isXs = !isSm && !isMd && !isLg;

  const [selectedIndex, setSelectedIndex] = React.useState(1);
  const [translateX, setTranslateX] = React.useState(
    isXs ? DEFAULT_TRANSLATION_MOBILE : DEFAULT_TRANSLATION_DESKTOP,
  );

  const dragContainer = (newIndex: number) => {
    const newTranslation = getTranslationInPx({
      toIndex: newIndex,
    });

    setTranslateX(newTranslation);
    setSelectedIndex(newIndex);
  };

  const goNext = () => {
    if (selectedIndex < courses.length - 1) {
      const newIndex = selectedIndex + 1;

      dragContainer(newIndex);
    } else {
      dragContainer(0);
    }
  };

  const goPrev = () => {
    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;

      dragContainer(newIndex);
    } else {
      dragContainer(courses.length - 1);
    }
  };

  const handleCardClick = ({ index, url }: { index: number; url: string }) => {
    if (index === selectedIndex) {
      window.location.pathname = url;
    } else {
      dragContainer(index);
    }
  };

  const ArrowLeftButton = (
    <IconButton
      isWhite
      withoutBorders
      onClick={goPrev}
      icon={{
        component: ArrowLeft,
        size: 32,
      }}
    />
  );

  const ArrowRightButton = (
    <IconButton
      isWhite
      withoutBorders
      onClick={goNext}
      icon={{
        component: ArrowRight,
        size: 32,
      }}
    />
  );

  if (!courses.length) {
    return <Loader />;
  }

  return (
    <Wrapper className="flex flex-col items-center justify-center relative overflow-hidden">
      <div
        className="absolute bottom-0 overflow-hidden w-full left-0 flex flex-col  items-center justify-center"
        css={`
          width: 848px;
          left: 50%;
          transform: translateX(-50%);
        `}
      >
        <SliderContainer className="overflow-hidden py-48 m-auto">
          <DraggableContentContainer
            translateX={translateX}
            className="flex flex-row items-start"
          >
            {courses.map(
              (
                {
                  originalId,
                  imagePlaceholder,
                  professor,
                  promoImage,
                  title,
                  url,
                },
                index,
              ) =>
                originalId && (
                  <SliderItem
                    handleClick={() =>
                      handleCardClick({ index, url: url || '' })
                    }
                    isSelected={selectedIndex === index}
                    key={originalId}
                    imagePlaceholder={imagePlaceholder || ''}
                    professorName={professor?.displayName || ''}
                    promoImage={promoImage || ''}
                    title={title}
                    originalId={originalId}
                  />
                ),
            )}
          </DraggableContentContainer>
        </SliderContainer>
        <div className="flex mt-12">
          {ArrowLeftButton}
          {ArrowRightButton}
        </div>
      </div>
    </Wrapper>
  );
};

const PacksCarousel: React.FC<React.PropsWithChildren<SliderProps>> = ({
  courses,
}) => {
  const isClient = useIsClient();

  if (!isClient) {
    return <Loader />;
  }

  return <Slider courses={courses} />;
};

export default React.memo(PacksCarousel);
